import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useApp } from '../../contexts/AppContext';

const TIMEZONES  = ['Asia/Kuala_Lumpur','Asia/Singapore','Asia/Jakarta','Asia/Bangkok','UTC','Asia/Tokyo','Europe/London','America/New_York'];
const CURRENCIES = ['MYR','USD','SGD','EUR','GBP','JPY'];
const LANGUAGES  = [['en','English'],['ms','Bahasa Malaysia'],['zh','Chinese'],['ta','Tamil']];
const PROTOCOLS  = ['LLRP','GS1-EPCIS','ISO-18000-6C','Impinj-Native'];

const EMOJI_LIST = ['📦','💻','🖨','📡','🔧','🔌','📱','🖥','⚙','🔋','📷','🔍','🗄','🖱','⌨','📺','🎮','🔦','🔑','🪝'];
const COLORS_LIST = ['#5b9cf6','#00e5a0','#f5a623','#e05757','#b89de8','#a0b4c0','#0a9e6e','#c0392b','#2980b9','#8e44ad'];

function Lbl({ children }) { return <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',fontWeight:600,marginBottom:6 }}>{children}</div>; }

function Modal({ title, sub, onClose, children }) {
  return <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.88)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:'#090e15',border:'1px solid #1a2a3a',borderRadius:14,width:'100%',maxWidth:600,maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column' }}>
      <div style={{ padding:'20px 24px',borderBottom:'1px solid #1a2a3a' }}>
        <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2' }}>{title}</div>
        {sub&&<div style={{ fontSize:11,color:'#7090a0',marginTop:3,fontFamily:"'IBM Plex Mono',monospace" }}>{sub}</div>}
      </div>
      <div style={{ padding:24,overflowY:'auto' }}>{children}</div>
    </div>
  </div>;
}

const Row = ({ label, sub, children }) => (
  <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 0',borderBottom:'1px solid #1a2a3a' }}>
    <div style={{ flex:1 }}>
      <div style={{ fontSize:13,fontWeight:600,color:'#c8d6e0' }}>{label}</div>
      {sub&&<div style={{ fontSize:11,color:'#7090a0',marginTop:3 }}>{sub}</div>}
    </div>
    <div style={{ flexShrink:0,marginLeft:24 }}>{children}</div>
  </div>
);

const Toggle = ({ val, onChange }) => (
  <div onClick={onChange} style={{ width:44,height:24,borderRadius:12,background:val?'#00e5a0':'#1a2a3a',position:'relative',cursor:'pointer',transition:'background .2s',border:val?'none':'1px solid #2a3f52',flexShrink:0 }}>
    <div style={{ position:'absolute',top:3,left:val?'calc(100% - 19px)':'3px',width:16,height:16,borderRadius:'50%',background:val?'#060b10':'#7090a0',transition:'left .2s' }}/>
  </div>
);

const Inp = ({ value, onChange, type='text', placeholder='', mono }) => (
  <input className="inp" type={type} value={value||''} placeholder={placeholder} onChange={e=>onChange(e.target.value)} style={{ width:240,fontFamily:mono?"'IBM Plex Mono',monospace":'inherit',fontSize:12 }}/>
);

const Sel = ({ value, onChange, options }) => (
  <select className="sel" value={value||''} onChange={e=>onChange(e.target.value)} style={{ minWidth:180,fontSize:12 }}>
    {options.map(o=>Array.isArray(o)?<option key={o[0]} value={o[0]}>{o[1]}</option>:<option key={o}>{o}</option>)}
  </select>
);

export default function SettingsPage() {
  const { settings, setSettings, saveSettings, facilities, showToast, profile } = useApp();
  const { features } = useTenant();
  const feat = features || {};
  const [tab,setTab]           = useState('general');
  const [form,setForm]         = useState({ ...settings });
  const [readerModal,setReaderModal]   = useState(null);
  const [readerForm,setReaderForm]     = useState({});
  const [readerSaving,setReaderSaving] = useState(false);
  const [dupWarning,  setDupWarning]   = useState(null);  // { ip, facilityName, readerName }
  const [carrierModal,setCarrierModal] = useState(null);
  const [carrierForm,setCarrierForm]   = useState({});
  const [atModal,setAtModal]     = useState(null);
  const [atForm,setAtForm]       = useState({});
  const [condModal,setCondModal] = useState(null);
  const [condForm,setCondForm]   = useState({});
  const [saving,setSaving]     = useState(false);
  const [bridgeStatus,setBridgeStatus] = useState(null);  // null | { readers, tagsToday, ... }
  const [bridgeUrl,setBridgeUrl]       = useState(localStorage.getItem('rfid_bridge_url') || 'http://localhost:3500');
  const [bridgeDownloadWin, setBridgeDownloadWin] = useState('');
  const [bridgeDownloadMac, setBridgeDownloadMac] = useState('');
  const [bridgeConnecting,setBridgeConnecting] = useState(false);

  // Load bridge download links from Supabase
  useEffect(() => {
    const loadBridgeLinks = async () => {
      try {
        const { supabase } = await import('../../supabaseClient');
        const { data } = await supabase.from('platform_settings')
          .select('key, value')
          .in('key', ['rfid_bridge_dl_win', 'rfid_bridge_dl_mac']);
        if (data) {
          const win = data.find(r => r.key === 'rfid_bridge_dl_win');
          const mac = data.find(r => r.key === 'rfid_bridge_dl_mac');
          if (win?.value) setBridgeDownloadWin(win.value);
          if (mac?.value) setBridgeDownloadMac(mac.value);
        }
      } catch(e) {}
    };
    loadBridgeLinks();
  }, []);

  // Poll bridge status every 5s when on RFID tab
  useEffect(() => {
    if (tab !== 'rfid') return;
    fetchBridgeStatus();
    const t = setInterval(fetchBridgeStatus, 5000);
    return () => clearInterval(t);
  }, [tab, bridgeUrl]);

  const fetchBridgeStatus = async () => {
    try {
      const res  = await fetch(`${bridgeUrl}/api/status`, { signal: AbortSignal.timeout(3000) });
      const data = await res.json();
      setBridgeStatus(data);
    } catch(e) {
      setBridgeStatus(null);
    }
  };

  const syncFromBridge = async () => {
    if (!bridgeStatus) return;
    setBridgeConnecting(true);
    try {
      // Get full reader list from bridge config
      const res  = await fetch(`${bridgeUrl}/api/config`);
      const cfg  = await res.json();
      const bridgeReaders = (cfg.readers || []).map((r, i) => ({
        id:           `RDR${String(i+1).padStart(3,'0')}`,
        name:         r.name,
        ip:           r.ip,
        port:         r.port || 9090,
        protocol:     'LLRP',
        facilityId:   r.facilityId || '',
        facilityName: r.facilityName || '',
        antennas:     4,
        status:       bridgeStatus.readers?.find(s => s.ip === r.ip)?.connected ? 'Online' : 'Offline',
        txPower:      cfg.antennaConfig?.txPower || 30,
      }));
      await save({ rfidReaders: bridgeReaders });
      showToast('✓ Synced from bridge — ' + bridgeReaders.length + ' reader(s)');
    } catch(e) {
      showToast('❌ Sync failed: ' + e.message);
    }
    setBridgeConnecting(false);
  };

  // Sync form when settings load from DB
  useEffect(() => { setForm(s => ({ ...s, ...settings })); }, [settings]);

  const save = async (patch) => {
    const updated = { ...form, ...patch };
    setForm(updated);
    setSaving(true);
    try {
      await saveSettings(updated);
    } catch(err){ showToast('❌ '+err.message,'error'); }
    setSaving(false);
  };

  const f = (key, val) => setForm(prev=>({ ...prev, [key]: val }));

  // Check if IP already used by another facility/reader
  const checkDuplicate = (ip, currentId = null) => {
    const readers = form.rfidReaders || [];
    return readers.find(r =>
      r.ip === ip &&
      r.id !== currentId
    ) || null;
  };

  // When IP changes in form — auto check duplicate
  const onIpChange = (ip) => {
    setReaderForm(f => ({ ...f, ip }));
    if (!ip) { setDupWarning(null); return; }
    const dup = checkDuplicate(ip, readerModal?.id);
    if (dup) {
      setDupWarning({ ip, facilityName: dup.facilityName || '—', readerName: dup.name });
    } else {
      setDupWarning(null);
    }
  };

  const saveReader = async () => {
    if (!readerForm.name || !readerForm.ip) return;

    // Block save if duplicate IP on different facility
    const dup = checkDuplicate(readerForm.ip, readerModal?.id);
    if (dup) {
      showToast(`❌ IP ${readerForm.ip} already used by "${dup.name}" at ${dup.facilityName}`);
      return;
    }

    setReaderSaving(true);
    const fac      = facilities.find(f => f.id === readerForm.facilityId);
    const readers  = form.rfidReaders || [];
    const newEntry = {
      ...readerForm,
      id:           readerModal === 'add' ? `RDR${String(readers.length+1).padStart(3,'0')}` : readerModal.id,
      facilityName: fac?.name || '',
      status:       'Online',
    };

    let updated;
    if (readerModal === 'add') {
      updated = [...readers, newEntry];
    } else {
      updated = readers.map(r => r.id === readerModal.id ? newEntry : r);
    }

    // Save to Supabase
    await save({ rfidReaders: updated });

    // Sync to RFID Bridge if it's running
    try {
      const bridgeKey = `${newEntry.ip}:${newEntry.port || 9090}`;

      if (readerModal !== 'add') {
        // Remove old entry first
        await fetch(`${bridgeUrl}/api/remove-reader`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: bridgeKey }),
        }).catch(() => {});
      }

      const res = await fetch(`${bridgeUrl}/api/add-reader`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:         newEntry.name,
          ip:           newEntry.ip,
          port:         newEntry.port || 9090,
          facilityId:   newEntry.facilityId,
          facilityName: newEntry.facilityName,
          orgId:        profile?.org_id,
        }),
      });
      if (res.ok) {
        showToast(`✓ Reader "${newEntry.name}" saved and connected to bridge`);
      } else {
        showToast(`✓ Reader saved — bridge offline, will connect when started`);
      }
    } catch {
      showToast(`✓ Reader saved — bridge offline, will connect when started`);
    }

    setReaderSaving(false);
    setReaderModal(null);
    setDupWarning(null);
  };

  const saveCarrier = () => {
    if(!carrierForm.name) return;
    const carriers = form.carriers||[];
    let updated;
    if(carrierModal==='add') {
      updated = [...carriers, { ...carrierForm, id:`CAR${String(carriers.length+1).padStart(3,'0')}` }];
    } else {
      updated = carriers.map(c=>c.id===carrierModal.id?{...c,...carrierForm}:c);
    }
    save({ carriers:updated });
    setCarrierModal(null);
  };

  const saveCondition = () => {
    if (!condForm.name) return;
    const conditions = form.assetConditions || [];
    let updated;
    if (condModal === 'add') {
      updated = [...conditions, { id: condForm.name.toLowerCase().replace(/\s+/g,'-'), name: condForm.name, color: condForm.color || '#5b9cf6' }];
    } else {
      updated = conditions.map(c => c.id === condModal.id ? { ...c, ...condForm } : c);
    }
    save({ assetConditions: updated });
    setCondModal(null);
  };

  const saveAssetType = () => {
    if(!atForm.name) return;
    const types = form.assetTypes||[];
    let updated;
    if(atModal==='add') {
      updated = [...types, { ...atForm, id:`AT${String(types.length+1).padStart(3,'0')}` }];
    } else {
      updated = types.map(t=>t.id===atModal.id?{...t,...atForm}:t);
    }
    save({ assetTypes:updated });
    setAtModal(null);
  };

  const TABS = [
    { id:'general',    icon:'⚙',  label:'General' },
    { id:'rfid',       icon:'📡', label:'RFID Devices' },
    { id:'assets',     icon:'📦', label:'Asset Types' },
    ...(feat.can_deliveries !== false ? [{ id:'deliveries', icon:'🚚', label:'Deliveries' }] : []),
    { id:'notify',     icon:'🔔', label:'Notifications' },
    { id:'security',   icon:'🔒', label:'Security' },
  ];

  return <div>
    <div style={{ marginBottom:24 }}>
      <div style={{ fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:'#e8edf2' }}>Settings</div>
      <div style={{ fontSize:12,color:'#7090a0',marginTop:4 }}>System, RFID devices, and configuration</div>
    </div>

    {/* Horizontal tab bar */}
    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:10 }}>
      <div style={{ display:'flex',gap:2,overflowX:'auto',flexWrap:'nowrap' }}>
        {TABS.map(t=>(
          <button key={t.id} className="tab-btn" onClick={()=>setTab(t.id)} style={{
            color:tab===t.id?'#00e5a0':'#7090a0',
            borderBottom:tab===t.id?'2px solid #00e5a0':'2px solid transparent',
            whiteSpace:'nowrap',
            fontSize:12,
          }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <button className="btn" style={{ background:'#00e5a0',color:'#060b10',fontSize:12,padding:'8px 20px',flexShrink:0 }} onClick={()=>save(form)} disabled={saving}>
        {saving?'Saving…':'💾 Save All'}
      </button>
    </div>

    <div style={{ display:'grid',gridTemplateColumns:'1fr',gap:20,alignItems:'start' }}>
      <div style={{ background:'#0d1520',border:'1px solid #1a2a3a',borderRadius:12,padding:24,minHeight:400 }}>

        {/* GENERAL */}
        {tab==='general'&&<>
          <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>General Settings</div>
          <div style={{ fontSize:12,color:'#7090a0',marginBottom:20 }}>Basic system configuration and display preferences</div>
          <Row label="Company Name" sub="Used in reports and labels"><Inp value={form.companyName} onChange={v=>f('companyName',v)} placeholder="Company Sdn. Bhd."/></Row>
          <Row label="Timezone" sub="Used for all timestamps"><Sel value={form.timezone} onChange={v=>f('timezone',v)} options={TIMEZONES}/></Row>
          <Row label="Currency" sub="Display currency for asset values"><Sel value={form.currency} onChange={v=>f('currency',v)} options={CURRENCIES}/></Row>
          <Row label="Language" sub="System interface language"><Sel value={form.language} onChange={v=>f('language',v)} options={LANGUAGES}/></Row>
          <Row label="Items Per Page" sub="Default rows shown in tables">
            <input className="inp" type="number" min={10} max={100} value={form.itemsPerPage||25} onChange={e=>f('itemsPerPage',Number(e.target.value))} style={{ width:90,fontFamily:"'IBM Plex Mono',monospace",fontSize:12 }}/>
          </Row>
          <div style={{ marginTop:20,display:'flex',justifyContent:'flex-end' }}>
            <button className="btn" style={{ background:'#00e5a0',color:'#060b10' }} onClick={()=>save(form)} disabled={saving}>{saving?'Saving…':'Save General Settings'}</button>
          </div>
        </>}

        {/* RFID DEVICES */}
        {tab==='rfid'&&<>

          {/* Bridge Download */}
          <div style={{ marginBottom:16, padding:'14px 18px', background:'#060b10', border:'1px solid #1a2a3a', borderRadius:10 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:13, color:'#e8edf2', marginBottom:12 }}>
              ⬇ RFID Bridge Download
            </div>
            <div style={{ fontSize:11, color:'#7090a0', marginBottom:14 }}>
              Install the RFID Bridge app on the PC connected to your RFID reader.
            </div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
              <a href={bridgeDownloadWin || '#'}
                download={bridgeDownloadWin ? 'rfid-bridge-windows.zip' : undefined}
                onClick={e => { if(!bridgeDownloadWin) { e.preventDefault(); showToast('❌ Windows download URL not set — configure below'); }}}
                style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px',
                  background:'#1a2a3a', border:'1px solid #2a3a4a', borderRadius:8,
                  color:'#e8edf2', fontWeight:700, fontSize:12, textDecoration:'none', cursor:'pointer' }}>
                🖥 Download for Windows
              </a>
              <a href={bridgeDownloadMac || '#'}
                download={bridgeDownloadMac ? 'rfid-bridge-mac.zip' : undefined}
                onClick={e => { if(!bridgeDownloadMac) { e.preventDefault(); showToast('❌ Mac download URL not set — configure below'); }}}
                style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px',
                  background:'#1a2a3a', border:'1px solid #2a3a4a', borderRadius:8,
                  color:'#e8edf2', fontWeight:700, fontSize:12, textDecoration:'none', cursor:'pointer' }}>
                🍎 Download for Mac
              </a>
            </div>
            {(!bridgeDownloadWin && !bridgeDownloadMac) && (
              <div style={{ fontSize:11, color:'#7090a0', marginTop:4 }}>
                ℹ Download links not configured yet — contact your SuperAdmin.
              </div>
            )}
          </div>

          {/* Bridge Connection Banner */}
          <div style={{ marginBottom:16, padding:'12px 16px', background:'#060b10', border:`1px solid ${bridgeStatus?'#00e5a030':'#1a2a3a'}`, borderRadius:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flex:1 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background: bridgeStatus ? '#00e5a0' : '#e05757', flexShrink:0 }}/>
                <div style={{ fontSize:13, fontWeight:700, color: bridgeStatus ? '#00e5a0' : '#e05757' }}>
                  {bridgeStatus ? '● Bridge Connected' : '○ Bridge Offline'}
                </div>
                {bridgeStatus && (
                  <div style={{ fontSize:11, color:'#7090a0' }}>
                    {bridgeStatus.connectedReaders} reader(s) · {bridgeStatus.tagsToday} tags today
                  </div>
                )}
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input
                  style={{ background:'#0d1520', border:'1px solid #1a2a3a', borderRadius:6, padding:'4px 10px',
                    fontSize:11, color:'#c8d6e0', fontFamily:"'IBM Plex Mono',monospace", width:200 }}
                  value={bridgeUrl}
                  onChange={e => { setBridgeUrl(e.target.value); localStorage.setItem('rfid_bridge_url', e.target.value); }}
                  placeholder="http://localhost:3500"
                />
                <button className="btn" style={{ fontSize:11, padding:'5px 10px', background:'#1a2a3a', color:'#c8d6e0' }}
                  onClick={fetchBridgeStatus}>↻</button>
                {bridgeStatus && (
                  <button className="btn" style={{ fontSize:11, padding:'5px 12px', background:'#5b9cf618', border:'1px solid #5b9cf630', color:'#5b9cf6' }}
                    onClick={syncFromBridge} disabled={bridgeConnecting}>
                    {bridgeConnecting ? '⏳ Syncing…' : '⬇ Sync from Bridge'}
                  </button>
                )}
              </div>
            </div>

            {/* Live reader status from bridge */}
            {bridgeStatus?.readers?.length > 0 && (
              <div style={{ marginTop:10, display:'flex', gap:8, flexWrap:'wrap' }}>
                {bridgeStatus.readers.map(r => (
                  <div key={r.key} style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 10px',
                    background: r.connected ? '#00e5a010' : '#e0575710',
                    border: `1px solid ${r.connected ? '#00e5a030' : '#e0575730'}`, borderRadius:20 }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background: r.connected ? '#00e5a0' : '#e05757' }}/>
                    <span style={{ fontSize:11, color:'#c8d6e0', fontWeight:600 }}>{r.name}</span>
                    <span style={{ fontSize:10, color:'#7090a0', fontFamily:"'IBM Plex Mono',monospace" }}>{r.ip}:{r.port}</span>
                    <span style={{ fontSize:10, color: r.connected ? '#00e5a0' : '#e05757', fontWeight:700 }}>
                      {r.connected ? (r.tagsRead > 0 ? `${r.tagsRead} tags` : 'idle') : 'offline'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20 }}>
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>RFID Devices</div>
              <div style={{ fontSize:12,color:'#7090a0' }}>{(form.rfidReaders||[]).length} readers configured · {(form.rfidReaders||[]).filter(r=>r.status==='Online').length} online</div>
            </div>
            <button className="btn" style={{ background:'#00e5a0',color:'#060b10',fontSize:12 }} onClick={()=>{setReaderForm({name:'',ip:'',port:'9090',protocol:'LLRP',facilityId:facilities[0]?.id||'',antennas:4,readInterval:500,txPower:30,sensitivity:-70,status:'Online'});setReaderModal('add');}}>+ Add Reader</button>
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {(form.rfidReaders||[]).map((r,i)=>{ const bridgeReader = bridgeStatus?.readers?.find(b => b.ip === r.ip || b.ip === `${r.ip}:${r.port}`); return (
              <div key={r.id} style={{ display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:'#060b10',border:`1px solid ${r.status==='Online'?'#00e5a030':'#e0575730'}`,borderRadius:10 }}>
                <div style={{ position:'relative',flexShrink:0 }}>
                  <div style={{ width:10,height:10,borderRadius:'50%',background:r.status==='Online'?'#00e5a0':'#e05757' }}/>
                  {r.status==='Online'&&<div style={{ position:'absolute',inset:-3,borderRadius:'50%',border:'2px solid #00e5a0',animation:'ping 1.5s infinite',opacity:.4 }}/>}
                </div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontWeight:700,fontSize:13,color:'#e8edf2' }}>{r.name}</div>
                  <div style={{ display:'flex',gap:12,fontSize:11,color:'#7090a0',marginTop:2,fontFamily:"'IBM Plex Mono',monospace" }}>
                    <span>{r.ip}:{r.port}</span>
                    <span>{r.protocol}</span>
                    <span>{r.facilityName}</span>
                    <span>{r.antennas} antennas</span>
                  </div>
                </div>
                <span style={{ fontSize:10,fontWeight:600,color:r.status==='Online'?'#00e5a0':'#e05757',border:`1px solid ${r.status==='Online'?'#00e5a040':'#e0575740'}`,borderRadius:6,padding:'2px 8px' }}>{r.status}</span>
                <div style={{ display:'flex',gap:6 }}>
                  <button className="btn" style={{ background:'#1a2a3a',color:'#c8d6e0',fontSize:11,padding:'5px 10px' }} onClick={()=>{setReaderForm({...r});setReaderModal(r);}}>✏</button>
                  <button className="btn" style={{ background:'#e0575720',border:'1px solid #e0575740',color:'#e05757',fontSize:11,padding:'5px 10px' }} onClick={async () => {
                    const r2 = (form.rfidReaders||[])[i];
                    // Remove from bridge
                    fetch(`${bridgeUrl}/api/remove-reader`, {
                      method:'POST', headers:{'Content-Type':'application/json'},
                      body: JSON.stringify({ key: `${r2.ip}:${r2.port||9090}` })
                    }).catch(()=>{});
                    save({ rfidReaders:(form.rfidReaders||[]).filter((_,j)=>j!==i) });
                  }}>🗑</button>
                </div>
              </div>
            );})} 
            {(form.rfidReaders||[]).length===0&&<div style={{ textAlign:'center',padding:'40px 0',color:'#7090a0' }}><div style={{ fontSize:28,marginBottom:10 }}>📡</div>No RFID readers configured. Add one to start tracking assets.</div>}
          </div>
        </>}

        {/* ASSET TYPES */}
        {tab==='assets'&&<>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20 }}>
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>Asset Types</div>
              <div style={{ fontSize:12,color:'#7090a0' }}>{(form.assetTypes||[]).length} types configured</div>
            </div>
            <button className="btn" style={{ background:'#00e5a0',color:'#060b10',fontSize:12 }} onClick={()=>{setAtForm({name:'',icon:'📦',color:'#5b9cf6'});setAtModal('add');}}>+ Add Type</button>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:10,marginBottom:16 }}>
            {(form.assetTypes||[]).map((at,idx)=>(
              <div key={at.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'12px 14px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:10 }}>
                <div style={{ width:40,height:40,borderRadius:10,background:at.color+'22',border:`1.5px solid ${at.color}55`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0 }}>{at.icon}</div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontWeight:700,fontSize:14,color:'#e8edf2' }}>{at.name}</div>
                  <div style={{ fontSize:10,color:'#7090a0',fontFamily:"'IBM Plex Mono',monospace",marginTop:2 }}>{at.id}</div>
                </div>
                <div style={{ width:10,height:10,borderRadius:'50%',background:at.color,flexShrink:0 }}/>
                <div style={{ display:'flex',gap:5 }}>
                  <button className="btn" style={{ background:'#1a2a3a',color:'#c8d6e0',fontSize:11,padding:'4px 8px' }} onClick={()=>{setAtForm({...at});setAtModal(at);}}>✏</button>
                  <button className="btn" style={{ background:'#e0575718',border:'1px solid #e0575730',color:'#e05757',fontSize:11,padding:'4px 8px' }} onClick={()=>save({assetTypes:(form.assetTypes||[]).filter((_,i)=>i!==idx)})}>🗑</button>
                </div>
              </div>
            ))}
            {(form.assetTypes||[]).length===0&&<div style={{ gridColumn:'1/-1',textAlign:'center',padding:'30px 0',color:'#7090a0' }}><div style={{ fontSize:28,marginBottom:8 }}>📦</div>No asset types. Add one to customise your asset registry.</div>}
          </div>
          <div style={{ background:'#f5a62310',border:'1px solid #f5a62330',borderRadius:8,padding:'10px 14px',fontSize:11,color:'#f5a623' }}>⚠ Removing a type does not affect existing assets — only removes it from the registration dropdown.</div>

          {/* ─── Asset Conditions ─────────────────────────────────────────── */}
          <div style={{ borderTop:'1px solid #1a2a3a',paddingTop:24,marginTop:8 }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16 }}>
              <div>
                <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>Asset Conditions</div>
                <div style={{ fontSize:12,color:'#7090a0' }}>{(form.assetConditions||['Excellent','Good','Fair','Needs Repair']).length} conditions configured</div>
              </div>
              <button className="btn" style={{ background:'#00e5a0',color:'#060b10',fontSize:12 }}
                onClick={()=>{setCondForm({name:'',color:'#5b9cf6'});setCondModal('add');}}>+ Add Condition</button>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10,marginBottom:16 }}>
              {(form.assetConditions||['Excellent','Good','Fair','Needs Repair'].map((n,i)=>({id:n.toLowerCase().replace(/\s+/g,'-'),name:n,color:['#00e5a0','#5b9cf6','#f5a623','#e05757'][i]}))).map((cond,idx)=>(
                <div key={cond.id||idx} style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#060b10',border:`1px solid ${cond.color||'#1a2a3a'}30`,borderRadius:10 }}>
                  <div style={{ width:10,height:10,borderRadius:'50%',background:cond.color||'#5b9cf6',flexShrink:0 }}/>
                  <div style={{ flex:1,fontWeight:700,fontSize:13,color:'#e8edf2' }}>{cond.name}</div>
                  <div style={{ display:'flex',gap:5 }}>
                    <button className="btn" style={{ background:'#1a2a3a',color:'#c8d6e0',fontSize:11,padding:'4px 8px' }}
                      onClick={()=>{setCondForm({...cond});setCondModal(cond);}}>✏</button>
                    <button className="btn" style={{ background:'#e0575718',border:'1px solid #e0575730',color:'#e05757',fontSize:11,padding:'4px 8px' }}
                      onClick={()=>save({assetConditions:(form.assetConditions||[]).filter((_,i)=>i!==idx)})}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Decommission Settings ────────────────────────────────────── */}
          <div style={{ borderTop:'1px solid #1a2a3a',paddingTop:24,marginTop:8 }}>
            <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>Decommission Settings</div>
            <div style={{ fontSize:12,color:'#7090a0',marginBottom:20 }}>Configure how decommissioned assets are handled</div>

            <Row label="Auto-hide after" sub="Decommissioned assets disappear from all lists after this period">
              <select className="sel" style={{ minWidth:160,fontSize:12 }}
                value={form.decommissionHide||'never'}
                onChange={e=>f('decommissionHide',e.target.value)}>
                <option value="never">Never hide</option>
                <option value="1">1 month</option>
                <option value="2">2 months</option>
                <option value="3">3 months</option>
              </select>
            </Row>

            <Row label="Require reason" sub="Force user to enter a reason when decommissioning">
              <Toggle val={form.decommissionRequireReason} onChange={()=>f('decommissionRequireReason',!form.decommissionRequireReason)}/>
            </Row>

            <Row label="Notify admin on decommission" sub="Send notification when any asset is decommissioned">
              <Toggle val={form.decommissionNotify} onChange={()=>f('decommissionNotify',!form.decommissionNotify)}/>
            </Row>

            <Row label="Allow EPC recycling" sub="Decommissioned asset EPCs can be reassigned to new assets">
              <Toggle val={form.epcRecycleEnabled} onChange={()=>f('epcRecycleEnabled',!form.epcRecycleEnabled)}/>
            </Row>
            {form.epcRecycleEnabled && (
              <Row label="Auto-release EPC on decommission" sub="EPC is immediately freed when asset is decommissioned, ready for reuse">
                <Toggle val={form.epcRecycleAutoRelease} onChange={()=>f('epcRecycleAutoRelease',!form.epcRecycleAutoRelease)}/>
              </Row>
            )}
            {form.epcRecycleEnabled && (
              <div style={{ background:'#00e5a010',border:'1px solid #00e5a030',borderRadius:8,padding:'10px 14px',fontSize:11,color:'#7090a0',marginBottom:8 }}>
                ♻ When recycling is enabled, EPCs from decommissioned assets appear as selectable options in the Register Asset form.
              </div>
            )}
            {form.decommissionHide && form.decommissionHide !== 'never' && (
              <div style={{ background:'#f5a62310',border:'1px solid #f5a62330',borderRadius:8,padding:'10px 14px',fontSize:11,color:'#f5a623',marginTop:8 }}>
                ⚠ Assets decommissioned for more than {form.decommissionHide} month{form.decommissionHide!=='1'?'s':''} will be hidden from Assets, Reports and Tracking pages. They remain in the database and can be found via search.
              </div>
            )}
          </div>
        </>}

        {/* DELIVERIES */}
        {tab==='deliveries'&&<>
          <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>Delivery Settings</div>
          <div style={{ fontSize:12,color:'#7090a0',marginBottom:20 }}>Configure carriers and delivery defaults</div>
          <Row label="Default Carrier" sub="Pre-selected when creating a delivery">
            <select className="sel" value={form.defaultCarrier||''} onChange={e=>f('defaultCarrier',e.target.value)} style={{ minWidth:180,fontSize:12 }}>
              {(form.carriers||[]).filter(c=>c.active).map(c=><option key={c.id}>{c.name}</option>)}
            </select>
          </Row>
          <Row label="Require Tracking Number" sub="Block creation if no tracking number entered">
            <Toggle val={form.requireTrackingNo} onChange={()=>f('requireTrackingNo',!form.requireTrackingNo)}/>
          </Row>
          <Row label="Auto-Create Delivery on Approval" sub="Create pending delivery when transfer request approved">
            <Toggle val={form.autoCreateDelivery} onChange={()=>f('autoCreateDelivery',!form.autoCreateDelivery)}/>
          </Row>
          <div style={{ marginTop:24,paddingTop:20,borderTop:'1px solid #1a2a3a' }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16 }}>
              <div>
                <div style={{ fontWeight:700,color:'#c8d6e0',fontSize:13 }}>Carrier List</div>
                <div style={{ fontSize:11,color:'#7090a0',marginTop:3 }}>{(form.carriers||[]).filter(c=>c.active).length} active · {(form.carriers||[]).filter(c=>!c.active).length} inactive</div>
              </div>
              <button className="btn" style={{ background:'#00e5a0',color:'#060b10',fontSize:12 }} onClick={()=>{setCarrierForm({name:'',type:'Domestic',contact:'',website:'',active:true});setCarrierModal('add');}}>+ Add Carrier</button>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
              {(form.carriers||[]).map((car,idx)=>(
                <div key={car.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'14px 16px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:10,opacity:car.active?1:0.55 }}>
                  <div style={{ width:40,height:40,borderRadius:10,background:car.active?'#00e5a015':'#1a2a3a',border:`1.5px solid ${car.active?'#00e5a040':'#2a3f52'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0 }}>🚚</div>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontWeight:700,fontSize:14,color:'#e8edf2' }}>{car.name}</div>
                    <div style={{ fontSize:11,color:'#a0b4c0',marginTop:2 }}>{car.type}{car.contact&&` · ${car.contact}`}</div>
                  </div>
                  <div style={{ display:'flex',gap:6 }}>
                    <button className="btn" style={{ background:car.active?'#1a2a3a':'#00e5a020',border:`1px solid ${car.active?'#2a3f52':'#00e5a040'}`,color:car.active?'#a0b4c0':'#00e5a0',fontSize:11,padding:'5px 10px' }} onClick={()=>{const u=(form.carriers||[]).map((c,i)=>i===idx?{...c,active:!c.active}:c);save({carriers:u});}}>{car.active?'⏸ Disable':'▶ Enable'}</button>
                    <button className="btn" style={{ background:'#1a2a3a',color:'#c8d6e0',fontSize:11,padding:'5px 10px' }} onClick={()=>{setCarrierForm({...car});setCarrierModal(car);}}>✏</button>
                    <button className="btn" style={{ background:'#e0575720',border:'1px solid #e0575740',color:'#e05757',fontSize:11,padding:'5px 10px' }} onClick={()=>save({carriers:(form.carriers||[]).filter((_,i)=>i!==idx)})}>🗑</button>
                  </div>
                </div>
              ))}
              {(form.carriers||[]).length===0&&<div style={{ textAlign:'center',padding:'30px 0',color:'#7090a0' }}><div style={{ fontSize:28,marginBottom:8 }}>🚚</div>No carriers. Add one to enable delivery tracking.</div>}
            </div>
          </div>
        </>}

        {/* NOTIFICATIONS */}
        {tab==='notify'&&<>
          <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>Notification Settings</div>
          <div style={{ fontSize:12,color:'#7090a0',marginBottom:20 }}>Configure system alerts and email notifications</div>
          <Row label="Email Notifications" sub="Send email alerts for key events"><Toggle val={form.emailNotifications} onChange={()=>f('emailNotifications',!form.emailNotifications)}/></Row>
          <Row label="Low Stock Alerts" sub="Alert when available asset count falls below threshold"><Toggle val={form.lowStockAlerts} onChange={()=>f('lowStockAlerts',!form.lowStockAlerts)}/></Row>
          <Row label="Maintenance Reminders" sub="Remind assigned users about scheduled maintenance"><Toggle val={form.maintenanceReminders} onChange={()=>f('maintenanceReminders',!form.maintenanceReminders)}/></Row>
          <Row label="Delivery Updates" sub="Email updates when delivery status changes"><Toggle val={form.deliveryUpdates} onChange={()=>f('deliveryUpdates',!form.deliveryUpdates)}/></Row>
          <Row label="Admin Email" sub="Where notification emails are sent"><Inp value={form.adminEmail} onChange={v=>f('adminEmail',v)} placeholder="admin@company.com"/></Row>
          <div style={{ marginTop:20,display:'flex',justifyContent:'flex-end' }}>
            <button className="btn" style={{ background:'#00e5a0',color:'#060b10' }} onClick={()=>save(form)} disabled={saving}>{saving?'Saving…':'Save Notifications'}</button>
          </div>
        </>}

        {/* SECURITY */}
        {tab==='security'&&<>
          <div style={{ fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:800,color:'#e8edf2',marginBottom:4 }}>Security Settings</div>
          <div style={{ fontSize:12,color:'#7090a0',marginBottom:20 }}>Authentication and access control configuration</div>
          <Row label="Session Timeout" sub="Auto-logout after inactivity">
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              <input className="inp" type="number" min={5} max={480} value={form.sessionTimeout||30} onChange={e=>f('sessionTimeout',Number(e.target.value))} style={{ width:90,fontFamily:"'IBM Plex Mono',monospace",fontSize:12 }}/>
              <span style={{ fontSize:11,color:'#7090a0' }}>minutes</span>
            </div>
          </Row>
          <Row label="Require Strong Passwords" sub="Minimum 8 chars, mixed case, numbers and symbols"><Toggle val={form.requireStrongPasswords} onChange={()=>f('requireStrongPasswords',!form.requireStrongPasswords)}/></Row>
          <Row label="Audit Log" sub="Track all user actions in the system"><Toggle val={form.auditLog} onChange={()=>f('auditLog',!form.auditLog)}/></Row>
          <div style={{ marginTop:20,display:'flex',justifyContent:'flex-end' }}>
            <button className="btn" style={{ background:'#00e5a0',color:'#060b10' }} onClick={()=>save(form)} disabled={saving}>{saving?'Saving…':'Save Security Settings'}</button>
          </div>
        </>}
      </div>
    </div>

    {/* RFID Reader Modal */}
    {readerModal&&<Modal title={readerModal==='add'?'Add RFID Reader':'Edit RFID Reader'} sub={readerModal!=='add'?readerModal.id:''} onClose={()=>{ setReaderModal(null); setDupWarning(null); }}>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
        <div style={{ gridColumn:'1/-1' }}><Lbl>Reader Name *</Lbl><input className="inp" value={readerForm.name||''} placeholder="e.g. Gate Reader – Main Entrance" onChange={e=>setReaderForm(f=>({...f,name:e.target.value}))}/></div>
        <div><Lbl>Facility</Lbl><select className="sel" style={{ width:'100%' }} value={readerForm.facilityId||''} onChange={e=>setReaderForm(f=>({...f,facilityId:e.target.value}))}>{facilities.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
        <div><Lbl>Protocol</Lbl><select className="sel" style={{ width:'100%' }} value={readerForm.protocol||'LLRP'} onChange={e=>setReaderForm(f=>({...f,protocol:e.target.value}))}>{PROTOCOLS.map(p=><option key={p}>{p}</option>)}</select></div>
        <div>
          <Lbl>IP Address *</Lbl>
          <input className="inp" style={{ fontFamily:"'IBM Plex Mono',monospace", borderColor: dupWarning ? '#e05757' : '' }}
            value={readerForm.ip||''} placeholder="192.168.1.100"
            onChange={e => onIpChange(e.target.value)}/>
          {dupWarning && (
            <div style={{ marginTop:5, fontSize:11, color:'#e05757', display:'flex', alignItems:'center', gap:6 }}>
              ⚠ This IP is already used by <strong>"{dupWarning.readerName}"</strong> at <strong>{dupWarning.facilityName}</strong>
            </div>
          )}
        </div>
        <div><Lbl>Port</Lbl><input className="inp" style={{ fontFamily:"'IBM Plex Mono',monospace" }} value={readerForm.port||'5084'} onChange={e=>setReaderForm(f=>({...f,port:e.target.value}))}/></div>
        <div><Lbl>No. of Antennas</Lbl><input className="inp" type="number" min={1} max={32} value={readerForm.antennas||4} onChange={e=>setReaderForm(f=>({...f,antennas:parseInt(e.target.value)}))}/></div>
        <div><Lbl>Status</Lbl><select className="sel" style={{ width:'100%' }} value={readerForm.status||'Online'} onChange={e=>setReaderForm(f=>({...f,status:e.target.value}))}><option>Online</option><option>Offline</option><option>Maintenance</option></select></div>
      </div>
      <div style={{ display:'flex',gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setReaderModal(null)}>Cancel</button>
        <button className="btn"
          style={{ flex:2, background: dupWarning ? '#e05757' : '#00e5a0', color:'#060b10', opacity: readerSaving ? 0.7 : 1 }}
          onClick={saveReader}
          disabled={readerSaving || !!dupWarning}>
          {readerSaving ? '⏳ Saving…' : dupWarning ? '⚠ Duplicate IP' : 'Save & Connect to Bridge'}
        </button>
      </div>
    </Modal>}

    {/* Carrier Modal */}
    {carrierModal&&<Modal title={carrierModal==='add'?'Add Carrier':'Edit Carrier'} onClose={()=>setCarrierModal(null)}>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14 }}>
        <div style={{ gridColumn:'1/-1' }}><Lbl>Carrier Name *</Lbl><input className="inp" value={carrierForm.name||''} placeholder="e.g. Pos Laju" onChange={e=>setCarrierForm(f=>({...f,name:e.target.value}))}/></div>
        <div><Lbl>Type</Lbl><select className="sel" style={{ width:'100%' }} value={carrierForm.type||'Domestic'} onChange={e=>setCarrierForm(f=>({...f,type:e.target.value}))}><option>Domestic</option><option>International</option></select></div>
        <div><Lbl>Status</Lbl><select className="sel" style={{ width:'100%' }} value={carrierForm.active?'Active':'Inactive'} onChange={e=>setCarrierForm(f=>({...f,active:e.target.value==='Active'}))}><option>Active</option><option>Inactive</option></select></div>
        <div><Lbl>Contact</Lbl><input className="inp" value={carrierForm.contact||''} placeholder="Phone or email" onChange={e=>setCarrierForm(f=>({...f,contact:e.target.value}))}/></div>
        <div><Lbl>Website</Lbl><input className="inp" value={carrierForm.website||''} placeholder="www.carrier.com" onChange={e=>setCarrierForm(f=>({...f,website:e.target.value}))}/></div>
      </div>
      <div style={{ display:'flex',gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setCarrierModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveCarrier}>Save Carrier</button>
      </div>
    </Modal>}


    {/* Condition Modal */}
    {condModal && <Modal title={condModal==='add'?'Add Condition':'Edit Condition'} onClose={()=>setCondModal(null)}>
      <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
        <div>
          <Lbl>Condition Name *</Lbl>
          <input className="inp" value={condForm.name||''} placeholder="e.g. Excellent, Good, Worn…"
            onChange={e=>setCondForm(f=>({...f,name:e.target.value}))}/>
        </div>
        <div>
          <Lbl>Colour</Lbl>
          <div style={{ display:'flex',gap:8,marginTop:4,flexWrap:'wrap' }}>
            {['#00e5a0','#5b9cf6','#f5a623','#e05757','#b89de8','#a0b4c0','#f472b6','#22d3ee'].map(c=>(
              <div key={c} onClick={()=>setCondForm(f=>({...f,color:c}))}
                style={{ width:28,height:28,borderRadius:6,background:c,cursor:'pointer',
                  border:condForm.color===c?'3px solid #fff':'2px solid transparent',
                  transform:condForm.color===c?'scale(1.15)':'none',transition:'all .15s' }}/>
            ))}
          </div>
        </div>
        {condForm.name && (
          <div style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'#060b10',border:`1px solid ${condForm.color||'#1a2a3a'}40`,borderRadius:8 }}>
            <div style={{ width:10,height:10,borderRadius:'50%',background:condForm.color||'#5b9cf6' }}/>
            <span style={{ fontWeight:700,fontSize:13,color:condForm.color||'#5b9cf6' }}>{condForm.name}</span>
          </div>
        )}
      </div>
      <div style={{ display:'flex',gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setCondModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveCondition}
          disabled={!condForm.name}>
          {condModal==='add'?'Add Condition':'Save Changes'}
        </button>
      </div>
    </Modal>}

    {/* Asset Type Modal */}
    {atModal&&<Modal title={atModal==='add'?'Add Asset Type':'Edit Asset Type'} onClose={()=>setAtModal(null)}>
      <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
        <div><Lbl>Type Name *</Lbl><input className="inp" value={atForm.name||''} placeholder="e.g. Laptop, Server, Forklift…" onChange={e=>setAtForm(f=>({...f,name:e.target.value}))}/></div>
        <div>
          <Lbl>Icon</Lbl>
          <div style={{ display:'flex',flexWrap:'wrap',gap:8,marginBottom:8 }}>
            {EMOJI_LIST.map(e=><button key={e} onClick={()=>setAtForm(f=>({...f,icon:e}))} style={{ width:36,height:36,borderRadius:8,background:atForm.icon===e?'#00e5a020':'#0d1520',border:`1.5px solid ${atForm.icon===e?'#00e5a0':'#1a2a3a'}`,fontSize:18,cursor:'pointer' }}>{e}</button>)}
          </div>
          <div style={{ fontSize:12,color:'#7090a0' }}>Selected: <span style={{ fontSize:18 }}>{atForm.icon||'📦'}</span></div>
        </div>
        <div>
          <Lbl>Color</Lbl>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
            {COLORS_LIST.map(c=><button key={c} onClick={()=>setAtForm(f=>({...f,color:c}))} style={{ width:30,height:30,borderRadius:6,background:c,border:atForm.color===c?`3px solid white`:'2px solid transparent',cursor:'pointer' }}/>)}
          </div>
        </div>
      </div>
      <div style={{ display:'flex',gap:10,marginTop:18 }}>
        <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setAtModal(null)}>Cancel</button>
        <button className="btn" style={{ flex:2,background:'#00e5a0',color:'#060b10' }} onClick={saveAssetType}>Save Type</button>
      </div>
    </Modal>}
  </div>;
}
